import React, { useLayoutEffect, useMemo, useRef } from 'react';
import Moment from 'react-moment';
import { Widget, TableHeaders, BaseUrl } from '../../models/WidgetModels';
import { DataTable } from 'motili-ui';
import { Tooltip } from 'react-tooltip';
import {
    snakeCaseToTitleCase,
    shortenedAddress,
} from '../../../utils/WidgetUtils';
import { useGetContractorDocumentDataQuery } from '../../store/api/ContractorExpiredDocumentsApi';
import useMenuButtonStyles from '../../components/menuButton/MenuButtonStyles';
import { QualificationSubTypes } from '../../models/ContractorDocumentExpirationModel';
import { PaginationInline, ShowingInline } from '../../components/paging/Paging';

const MIN_TABLE_WIDTH = 1200;

const raf = () => new Promise<void>(r => requestAnimationFrame(() => r()));
async function waitForStable(el: HTMLElement) {
    for (let i = 0; i < 40; i++) {
        const w1 = el.clientWidth;
        const o1 = getComputedStyle(el).opacity;
        await raf(); await raf();
        const w2 = el.clientWidth;
        const hasHead = !!el.querySelector('thead th');
        const hasRow  = !!el.querySelector('tbody tr');
        if (o1 === '1' && w1 === w2 && w2 > 0 && hasHead && hasRow) return;
    }
}

const setImp = (s: CSSStyleDeclaration, k: string, v: string) =>
    s.setProperty(k, v, 'important');

function normalizeTbody(root: HTMLElement) {
    root.querySelectorAll('table.motili-data-table > tbody').forEach((tb) => {
        const el = tb as HTMLElement;
        el.classList.remove('iyxFJp');
        setImp(el.style, 'float', 'none');
        setImp(el.style, 'display', 'table-row-group');
        setImp(el.style, 'line-height', 'normal');
        setImp(el.style, 'font-style', 'normal');
        setImp(el.style, 'clear', 'none');
        setImp(el.style, 'position', 'static');
        setImp(el.style, 'width', 'auto');
    });
}

function relaxFlexAncestors(node: HTMLElement) {
    let el: HTMLElement | null = node;
    for (let i = 0; i < 6 && el; i++) {
        const p = el.parentElement as HTMLElement | null;
        if (!p) break;
        const cs = getComputedStyle(p);
        if (cs.display === 'flex' || cs.display === 'inline-flex') {
            setImp(p.style, 'min-width', '0');
            setImp(p.style, 'overflow', 'visible');
        }
        el = p;
    }
}

function forceHorizontalOnlyScroll(root: HTMLElement) {
    const scroller = root.closest('.ef-table-scroll') as HTMLElement | null;
    const table = root.querySelector('table') as HTMLTableElement | null;
    if (!scroller || !table) return;
    setImp(scroller.style, 'min-width', '0');
    setImp(scroller.style, 'width', '100%');
    setImp(scroller.style, 'overflow-x', 'auto');
    setImp(scroller.style, 'overflow-y', 'hidden');
    setImp(scroller.style, 'max-height', 'none');
    setImp(scroller.style, '-webkit-overflow-scrolling', 'touch');
    setImp(scroller.style, 'scrollbar-gutter', 'stable');
    setImp(scroller.style, 'background', '#fff');
    setImp(scroller.style, 'border-radius', '0');

    relaxFlexAncestors(scroller);

    // ----- TABLE CORE -----
    table.classList.remove('table', 'table-hover');
    table.classList.add('motili-data-table');
    setImp(table.style, 'table-layout', 'fixed');
    setImp(table.style, 'border-collapse', 'collapse');
    setImp(table.style, 'border-spacing', '0');
    setImp(table.style, 'width', 'auto');
    setImp(table.style, 'min-width', `${MIN_TABLE_WIDTH}px`);

    table.querySelectorAll<HTMLTableCellElement>('thead th').forEach(th => {
        setImp(th.style, 'background', '#0E2248');
        setImp(th.style, 'color', '#FFFFFF');
        setImp(th.style, 'vertical-align', 'middle');
        setImp(th.style, 'padding', '8px 12px');
        setImp(th.style, 'line-height', '1.3');
        setImp(th.style, 'box-sizing', 'border-box');
    });

    // body cells: nowrap + ellipsis
    table.querySelectorAll<HTMLTableCellElement>('th, td').forEach(cell => {
        setImp(cell.style, 'padding', '8px 12px');
        setImp(cell.style, 'line-height', '1.4');
        setImp(cell.style, 'box-sizing', 'border-box');
        setImp(cell.style, 'white-space', 'nowrap');
        setImp(cell.style, 'overflow', 'hidden');
        setImp(cell.style, 'text-overflow', 'ellipsis');
    });

    table.querySelectorAll<HTMLTableCellElement>('tbody td').forEach(td => {
        setImp(td.style, 'vertical-align', 'top');
        Array.from(td.children).forEach((child) => {
            const el = child as HTMLElement;
            setImp(el.style, 'display', 'inline-flex');
            setImp(el.style, 'align-items', 'center');
            setImp(el.style, 'gap', '6px');
            setImp(el.style, 'margin', '0');
            setImp(el.style, 'padding', '0');
            setImp(el.style, 'max-width', '100%');
            setImp(el.style, 'white-space', 'inherit');
            setImp(el.style, 'line-height', 'inherit');
            setImp(el.style, 'overflow', 'hidden');
            setImp(el.style, 'text-overflow', 'ellipsis');
        });
    });
}

interface ContractorDocumentExpirationTableProps {
    mappedBucketCountData: { label: string; value: string; count: number }[];
    activeTablePage: number;
    onPageClick: (e: number) => void;
    documentFilters: object;
    chartSelectedValue: string;
    recordsPerPage: number;
    data: Widget;
}

export const ContractorDocumentExpirationTable = (
    props: ContractorDocumentExpirationTableProps
): JSX.Element => {
    const styles = useMenuButtonStyles();
    const {
        mappedBucketCountData,
        activeTablePage,
        onPageClick,
        documentFilters,
        chartSelectedValue,
        recordsPerPage,
        data,
    } = props;

    const totalNumberOfRecords:
        | {
              label: string;
              value: string;
              count: number;
          }[]
        | undefined = mappedBucketCountData?.filter(
        obj => obj.value === chartSelectedValue
    );
    const totalCount = useMemo(() => {
        return mappedBucketCountData?.find(o => o.value === chartSelectedValue)?.count ?? 0;
    }, [mappedBucketCountData, chartSelectedValue]);

    const tableHeaders: TableHeaders[] = [
        {
            displayName: 'Document Type',
            value: 'documentType',
            sortable: true,
            ascending: false,
        },
        {
            displayName: 'Name',
            value: 'name',
            sortable: true,
            ascending: false,
        },
        {
            displayName: 'Contractor Status',
            value: 'status',
            sortable: true,
            ascending: false,
        },
        {
            displayName: 'Until Expiration',
            value: 'daysUntilExpiration',
            sortable: true,
            ascending: false,
        },
        {
            displayName: 'Expiration Date',
            value: 'expirationDate',
            sortable: true,
            ascending: false,
        },
    ];

    const { data: GetFilteredContractorDocumentData } =
        useGetContractorDocumentDataQuery(
            {
                ...documentFilters,
                page: activeTablePage - 1,
                expirationBucket: chartSelectedValue,
            },
            { refetchOnMountOrArgChange: true }
        );

    function formatPhoneNumber(phoneNumber: string): string {
        const areaCode = phoneNumber.slice(0, 3);
        const firstPart = phoneNumber.slice(3, 6);
        const secondPart = phoneNumber.slice(6);

        return `(${areaCode})-${firstPart}-${secondPart}`;
    }

    const mappedWorkOrderCountData:
        | []
        | Array<{
              daysUntilExpiration: string;
              documentType: React.ReactElement;
              expirationDate: React.ReactElement;
              name: React.ReactElement;
              status: string;
          }> = useMemo(() => {
        if (GetFilteredContractorDocumentData) {
            return GetFilteredContractorDocumentData.result.map(
                (record, index) => {
                    const contractor =
                        record.contractor ?? record.resolvedContractor;
                    const contractorLink = `/#/ops/contractors/${contractor.id}/details`;
                    const contractorTeamMemberLink = `/#/ops/contractors/${contractor.id}/team`;
                    const stdAddress = shortenedAddress(
                        contractor.address.stdDeliveryLine
                    );
                    const cityStateZip = `${contractor.address.stdCity}, ${contractor.address.stdRegion} ${contractor.address.stdPostalCode}`;
                    const documentTypeDisplay =
                        record.category === 'Qualifications'
                            ? QualificationSubTypes[record.qualificationType]
                            : record.category || record.label;
                    let phoneNumberToDisplay;
                    switch (data.config.documentType) {
                        case 'CONTRACTOR_COMPANY':
                            phoneNumberToDisplay =
                                contractor.phoneContacts[0].phoneNumber;
                            break;
                        case 'CONTRACTOR_TEAM':
                            phoneNumberToDisplay = record.user?.mobile
                                ? formatPhoneNumber(record.user.mobile)
                                : record.contractorPhone;
                            break;
                        default:
                            phoneNumberToDisplay = null;
                            break;
                    }

                    const infoCardContent = (
                        <div
                            id='menu-list-quote'
                            style={{ maxWidth: 'fit-content' }}
                        >
                            <div className={styles.menuItemStyle}>
                                <span className={styles.menuItemIconStyle}>
                                    <img
                                        alt='phone icon'
                                        src={`${BaseUrl.widgetImage}/phone-Icon.svg`}
                                    />
                                </span>
                                <span className='menuItemText menuItemTextStyle'>
                                    {phoneNumberToDisplay || 'No phone number'}
                                </span>
                            </div>
                            <div className={styles.menuItemStyle}>
                                <span className={styles.menuItemIconStyle}>
                                    <img
                                        alt='home icon'
                                        src={`${BaseUrl.widgetImage}/home-Icon.svg`}
                                    />
                                </span>
                                <div className={styles.menuItemTextStyle}>
                                    <span>{stdAddress}</span>
                                    <br />
                                    <span>{cityStateZip}</span>
                                </div>
                            </div>
                        </div>
                    );

                    return {
                        documentType: (
                            <div>
                                <img
                                    id={`con-doc-exp-info-${contractor.id}-${index}`}
                                    key={index}
                                    alt='info icon'
                                    className={styles.infoTipStyle}
                                    src={`${BaseUrl.widgetImage}/info-icon.svg`}
                                />
                                <Tooltip
                                    id={`con-doc-exp-info-tooltip-${contractor.id}-${index}`}
                                    anchorSelect={`#con-doc-exp-info-${contractor.id}-${index}`}
                                    openOnClick
                                    place='bottom-start'
                                    opacity={1}
                                    style={{
                                        backgroundColor: '#0e2248',
                                        zIndex: 5,
                                        borderRadius: 6,
                                        maxWidth: 'fit-content',
                                    }}
                                    disableStyleInjection={'core'}
                                >
                                    {infoCardContent}
                                </Tooltip>

                                {documentTypeDisplay}
                            </div>
                        ),
                        status: snakeCaseToTitleCase(contractor.status),
                        name:
                            data.config.documentType === 'CONTRACTOR_TEAM' ? (
                                <a
                                    href={contractorTeamMemberLink}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                >
                                    {record.user
                                        ? `${record.user.firstName} ${record.user.lastName}`
                                        : record.contractorTeamFullName}
                                </a>
                            ) : (
                                <a
                                    href={contractorLink}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                >
                                    {contractor.name}
                                </a>
                            ),
                        daysUntilExpiration:
                            record.daysToExpiration >= 0 ? (
                                `${record.daysToExpiration} Days`
                            ) : (
                                <div style={{ color: 'red' }}>Expired</div>
                            ),
                        expirationDate: (
                            <Moment format='MMM D, YYYY'>
                                {record.expiration}
                            </Moment>
                        ),
                    };
                }
            );
        }

        return [];
    }, [GetFilteredContractorDocumentData, data.config.documentType, styles]);
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const wrapperWidth = 0;
    const hostRef = useRef<HTMLDivElement | null>(null);
    const dataTableKey = useMemo(
        () => `ef-${chartSelectedValue}-${activeTablePage}-${recordsPerPage}-${wrapperWidth}`,
        [chartSelectedValue, activeTablePage, recordsPerPage, wrapperWidth]
    );
    // Wait for the container to stabilize, then remove Bootstrap classes and apply inline styles
    useLayoutEffect(() => {
        const root = hostRef.current;
        if (!root) return;

        const apply = () => {
            const table = root.querySelector('table');
            if (!table) return;
            table.classList.remove('table', 'table-hover');
            table.classList.add('motili-data-table');
            normalizeTbody(root);
            forceHorizontalOnlyScroll(root);
        };

        let cancelled = false;
        (async () => { await waitForStable(root); if (!cancelled) apply(); })();

        const mo = new MutationObserver(() => {
            apply();
        });
        mo.observe(root, { childList: true, subtree: true });

        return () => { cancelled = true; mo.disconnect(); };
    }, [dataTableKey]);

    return (
        <div className="ef-scope" ref={wrapperRef} style={{ width: '100%' }}>
            <div className="ef-table-scroll">
                <div className="ef-host" ref={hostRef}>
                    <DataTable
                        headerBackground='#0E2248'
                        headerTextColor='#FFFFFF'
                        data-test-id='CDEWidgetClosedTable'
                        totalRecordCount={totalNumberOfRecords[0]?.count}
                        records={mappedWorkOrderCountData}
                        tableHeaders={tableHeaders}
                        showPagination={false}
                        activePage={activeTablePage}
                        recordsPerPage={recordsPerPage}
                        onPageClick={pageNumber => {
                            onPageClick(pageNumber);
                        }}
                        onListItemClick={() => {}}
                        onTableHeaderItemClick={() => {}}
                    />
                </div>
            </div>
            <div style={{  display:'flex', alignItems:'center', justifyContent:'space-between', width:'100%' }}>
                <ShowingInline
                    total={totalCount || 0}
                    page={activeTablePage}
                    perPage={recordsPerPage}
                />
                <div style={{ flex: 1 }} />
                <PaginationInline
                    total={totalCount || 0}
                    page={activeTablePage}
                    perPage={recordsPerPage}
                    onChange={onPageClick}
                />
            </div>
        </div>
    );
};
